/** All types that an exercise can have. */
export type ExerciseType = "Correction" | "FillBlanks" | "MultipleChoice" | "Order" | "Quiz";
export const ExerciseTypeValues = ["Correction", "FillBlanks", "MultipleChoice", "Order", "Quiz"];

export type ExerciseQuestionType =
    FillBlanksExerciseQuestion | MultipleChoiceExerciseQuestion | OrderExerciseQuestion | CorrectionExerciseQuestion | QuizExerciseQuestion;

export type IExerciseQuestionType =
    IFillBlanksExerciseQuestion | IMultipleChoiceExerciseQuestion | IOrderExerciseQuestion | ICorrectionExerciseQuestion | IQuizExerciseQuestion;

/** The numbers that a difficulty can have. */
export type DifficultyLevel = 1 | 2 | 3;

/**
 * Every exercise has to have these properties. They are normally populated through the args given within the exercise constructor.
 */
export interface IExercise {
    _id?: string;
    /** The title of a whole exercise. Like `Der, Die oder Das?`. */
    title: string;
    /** The description of this exercise. Like `Finde den korrekten Artikel für das angegebene Wort.` */
    description?: string;
    /** An array of questions. */
    questions: Array<IExerciseQuestionType>;
    /** An array of tags that exerciseQuestions have to have to be related to this exercise. */
    tags: Array<string>;
    /** The max number of questions within this exercise. */
    maxProgress: number;
    /** The difficulty of the exercise */
    difficulty: 1 | 2 | 3;
    /** The author/creator of this exercise (teacher's user ID) */
    author?: string;
}

/**
 * Each exercise can have multiple questions to be answered. This interface is for those questions.
 * It has a `question` which is to be displayed, a `solution` which the app has to compare to the user input and a `hint`
 * to be shown if the user doesn't have a clue.
 */
export interface IExerciseQuestion<Q, S extends IEquals | string | number | Array<unknown>> {
    _id?: string;
    /** The question (normally a string) for this exercise part. */
    question: Q;
    /** The solution (normally a string) for this exercise part. */
    solution: S;
    /** A representation of the solution as a string. */
    solutionString: string;
    /** The type of this exercise. */
    type: ExerciseType;
    /** The difficulty level of an exercise question. */
    difficulty: DifficultyLevel;
    /** The tags of an exercise question used to identify its purpose. */
    tags: Array<string>;
    /** The hint that can be shown if the user doesn't know how to do anything. */
    hint?: string;
    /** Did the user answer the question correctly? */
    isCorrect?: boolean;
    /**  The solution that the user submitted */
    userAnswer?: S;
    task?: string;
}

/**
 * Simple interface to have an equals-mechanism for non primitive types.
 */
export interface IEquals {
    /** Checks if the given value is equals the value executing this. */
    isEquals(value: IEquals): boolean;
}

/**
 * Class for the exercises. Just has some properties and helper functions.
 */
export class Exercise implements IExercise {
    public _id?: string;
    public title: string;
    public task?: string;
    public source?: string;
    public questions: Array<IExerciseQuestionType>;
    public tags: Array<string>;
    public maxProgress: number;
    public description?: string;
    public difficulty: 1 | 2 | 3;
    public author?: string;
    public done_workout?: boolean = false;

    public constructor(args: IExercise) {
        this._id = args._id;
        this.title = args.title;
        this.questions = (args.questions ?? []).map(question => {
            // Wenn es sich um eine ID (string, number) handelt, einfach übernehmen
            if (typeof question === 'string' || typeof question === 'number') {
                return question;
            }
            // Ansonsten wie gehabt mappen
            switch (question.type) {
                case "Correction":
                    return CorrectionExerciseQuestion.fromObject(question as unknown as ICorrectionExerciseQuestion);
                case "FillBlanks":
                    return FillBlanksExerciseQuestion.fromObject(question as unknown as IFillBlanksExerciseQuestion);
                case "MultipleChoice":
                    return MultipleChoiceExerciseQuestion.fromObject(question as unknown as IMultipleChoiceExerciseQuestion);
                case "Quiz":
                    return QuizExerciseQuestion.fromObject(question as unknown as IQuizExerciseQuestion);
                default:
                    return OrderExerciseQuestion.fromObject(question as unknown as IOrderExerciseQuestion);
            }
        });
        this._id = (args as any)._id;
        this.tags = args.tags;
        // Bei Teacher Exercises: maxProgress aus der Anzahl der Fragen berechnen, falls nicht gesetzt
        this.maxProgress = args.maxProgress || this.questions.length;
        this.description = args.description;
        this.difficulty = args.difficulty;
        this.author = (args as any).author;
    }
}

/**
 * Abstract top class for every subclass of the exercises. Holds the most important properties and has some small helper functions.
 * Is a generic class, which means you can give it an type `Q` for the question and `S` that will be used as type for the solution.
 */
export abstract class ExerciseQuestion<Q, S extends IEquals | string | number | Array<unknown>> implements IExerciseQuestion<Q, S> {
    public _id?: string;
    public question: Q;
    public solution: S;
    public solutionString: string;
    public type: ExerciseType;
    public currentSolution?: S;
    public difficulty: DifficultyLevel;
    public tags: Array<string>;
    public hint?: string;
    public options?: Array<string> | Array<Array<string>>;
    public isCorrect?: boolean;
    public userAnswer?: S;
    public task?: string;

    public constructor(args: IExerciseQuestion<Q, S>) {
        this._id = args._id;
        this.question = args.question;
        this.solution = args.solution;
        this.solutionString = args.solutionString;
        this.type = args.type;
        this.difficulty = args.difficulty;
        this.tags = args.tags;
        this.hint = args.hint;
        this.isCorrect = args.isCorrect;
        this.task = (args as any).task;
    }

    public convertCurrentSolutionToArrayWithOneString(currentSolution: any): Array<String>{
        let currentSolutionString: string = currentSolution.join(' ')
        let arrayWithOneString: Array<string> = [currentSolutionString]
        return arrayWithOneString;
    }

    /**
     * Checks if the given answer is equals the solution.
     */
    public abstract isSolutionCorrect(): boolean;
}

export interface IOrderExerciseQuestion extends IExerciseQuestion<Array<string>, Array<string>> {}

/**
 * Class for the exercises questions where some given words have to be put in order.
 */
export class OrderExerciseQuestion extends ExerciseQuestion<Array<string>, Array<string>> {

    public constructor(_id: string | undefined, question: Array<string>, solution: Array<string>, solutionString: string,
                       difficulty: DifficultyLevel, tags: Array<string>, hint?: string, task?: string) {
        super({ _id, question, solution, solutionString, type: "Order", difficulty, tags, hint, task });
        this.currentSolution = question;
    }

    /**
     * Creates a new instance of this class from a json object.
     *
     * @param currentQuestion - The object to create a class instance from.
     */
    public static fromObject(currentQuestion: IOrderExerciseQuestion): OrderExerciseQuestion {
        // Randomize order of elements.
        let randomized: string[];
        let tries = 0;
        do {
            randomized = currentQuestion.solution.slice().sort((_, __) => Math.random() - 0.5);
            tries++;
            // Maximal 10 Versuche, dann akzeptieren wir das Ergebnis
            if (tries > 10) break;
        } while (randomized.toString() === currentQuestion.solution.toString());

        currentQuestion.question = randomized;
        return new OrderExerciseQuestion(
            currentQuestion._id,
            currentQuestion.question,
            currentQuestion.solution,
            currentQuestion.solutionString,
            currentQuestion.difficulty,
            currentQuestion.tags,
            currentQuestion.hint,
            currentQuestion.task
        );
    }

    /**
     * Checks if the given answer is equals the solution.
     */
    public isSolutionCorrect(): boolean {
        this.userAnswer = this.currentSolution;
        if (!this.currentSolution || !Array.isArray(this.currentSolution)) {
            return false;
        }
        
        // Vergleiche die Arrays elementweise
        if (this.currentSolution.length !== this.solution.length) {
            return false;
        }
        
        for (let i = 0; i < this.currentSolution.length; i++) {
            if (this.currentSolution[i] !== this.solution[i]) {
                return false;
            }
        }
        return true;
    }
}

export interface IMultipleChoiceExerciseQuestion extends IExerciseQuestion<Array<string | null>, string> {
    options: Array<string>;
    task?: string;
}

/**
 * Class to be used for multiple choice exercises where one of the given options is the solution.
 */
export class MultipleChoiceExerciseQuestion extends ExerciseQuestion<Array<string | null>, string>
        implements IMultipleChoiceExerciseQuestion {

    public constructor(_id: string | undefined, public options: Array<string>, question: Array<string | null>, solution: string,
                       solutionString: string, difficulty: DifficultyLevel, tags: Array<string>, hint: string | undefined, task?: string) {
        super({ _id, question, solution, solutionString, hint, type: "MultipleChoice", tags, difficulty, task });
    }

    /**
     * Creates a new instance of this class from a json object.
     *
     * @param currentQuestion - The object to create a class instance from.
     */
    public static fromObject(currentQuestion: IMultipleChoiceExerciseQuestion): MultipleChoiceExerciseQuestion {
        return new MultipleChoiceExerciseQuestion(currentQuestion._id, currentQuestion.options, currentQuestion.question,
            currentQuestion.solution, currentQuestion.solutionString, currentQuestion.difficulty, currentQuestion.tags,
            currentQuestion.hint, currentQuestion.task);
    }

    /**
     * Getter für die visuelle Darstellung der Frage mit ___ statt null
     */
    public get displayQuestion(): string {
        if (Array.isArray(this.question)) {
            return this.question.map(part => {
                if (part === null || part === "null") {
                    return "___";
                }
                return part;
            }).join(" ");
        }
        return this.question as any;
    }

    /**
     * Override isSolutionCorrect to support both standard multiple choice and fill-in-the-blank sentences
     */
    public isSolutionCorrect(): boolean {
        this.userAnswer = this.currentSolution;
        
        if (!this.currentSolution) {
            return false;
        }
        
        // Fall 1: Lückensatz mit "null" als Platzhalter - question ist Array mit String
        if (Array.isArray(this.question) && typeof this.currentSolution === "string") {
            // Prüfe ob die Frage "null" enthält
            if (this.question[0] && this.question[0].includes("null")) {
                // Ersetze "null" durch die Userantwort
                const userSentence = this.question[0].replace("null", this.currentSolution);
                const result = userSentence.trim() === this.solutionString.trim();
                return result;
            }
        }
        
        // Fall 2: Lückensatz mit null-Werten - question ist Array mit null-Werten
        if (Array.isArray(this.question) && this.question.some(part => part === null) && typeof this.currentSolution === "string") {
            // Baue den kompletten Satz zusammen, indem null-Werte durch die Userantwort ersetzt werden
            const userSentence = this.question.map(part => part === null ? this.currentSolution : part).join(" ");
            const result = userSentence.trim() === this.solutionString.trim();
            return result;
        }
        
        // Fall 3: Standard MultipleChoice - direkter Vergleich mit der Lösung
        // Für Standard MultipleChoice: Vergleiche die User-Antwort direkt mit der solution
        
        // Konvertiere solution zu String falls es ein Array ist (für User-generated MultipleChoice)
        let solutionString = this.solution;
        if (Array.isArray(this.solution)) {
            solutionString = this.solution.join(' ');
        }
        
        // Direkter Vergleich mit der solution (nicht solutionString!)
        const directComparison = this.currentSolution === solutionString;
        const stringComparison = String(this.currentSolution).trim() === String(solutionString).trim();
        
        // Für Standard MultipleChoice verwenden wir nur den direkten Vergleich mit solution
        const result = directComparison || stringComparison;
        return result;
    }
}

export interface IQuizExerciseQuestion extends IExerciseQuestion<Array<string | null>, string> {
    options: Array<string>;
    task?: string;
}

/**
 * Class to be used for multiple choice exercises where one of the given options is the solution.
 */
export class QuizExerciseQuestion extends ExerciseQuestion<Array<string | null>, string>
        implements IQuizExerciseQuestion {

    public constructor(_id: string | undefined, public options: Array<string>, question: Array<string | null>, solution: string,
                       solutionString: string, difficulty: DifficultyLevel, tags: Array<string>, hint: string | undefined, task?: string) {
        super({ _id, question, solution, solutionString, hint, type: "Quiz", tags, difficulty, task });
    }

    /**
     * Creates a new instance of this class from a json object.
     *
     * @param currentQuestion - The object to create a class instance from.
     */
    public static fromObject(currentQuestion: IQuizExerciseQuestion): QuizExerciseQuestion {
        // Falls solutionString nicht vorhanden, verwende solution
        if (!currentQuestion.solutionString) {
            currentQuestion.solutionString = currentQuestion.solution;
        }
        
        return new QuizExerciseQuestion(currentQuestion._id, currentQuestion.options, currentQuestion.question,
            currentQuestion.solution, currentQuestion.solutionString, currentQuestion.difficulty, currentQuestion.tags,
            currentQuestion.hint, currentQuestion.task);
    }

    /**
     * Checks if the given answer is equals the solution.
     */
    public isSolutionCorrect(): boolean {
        this.userAnswer = this.currentSolution;
        if (!this.currentSolution) {
            return false;
        }
        // Mehrere Vergleichsmethoden für Robustheit
        const directComparison = this.currentSolution === this.solution;
        const stringComparison = String(this.currentSolution).trim() === String(this.solution).trim();
        const solutionStringComparison = String(this.currentSolution).trim() === String(this.solutionString).trim();
        
        // Verwende die strengste Übereinstimmung
        const isCorrect = directComparison || stringComparison || solutionStringComparison;
        return isCorrect;
    }
}

export interface IFillBlanksExerciseQuestion extends IExerciseQuestion<Array<string | null>, Array<string | null>> {
    /** Optional: Für jede Lücke ein Array von Auswahlmöglichkeiten (Dropdown), oder undefined für normale Eingabe. */
    optionsPerBlank?: Array<Array<string>|undefined>;
    /** Optional: Ursprüngliche Options aus der Datenbank */
    options?: Array<string> | Array<Array<string>>;
}

export class FillBlanksExerciseQuestion extends ExerciseQuestion<Array<string | null>, Array<string | null>>
            implements IFillBlanksExerciseQuestion {
    public currentSolution: string[];
    public correctSolutions: string[];
    public optionsPerBlank?: Array<Array<string>|undefined>;

    public constructor(_id: string | undefined, question: Array<string | null>, solution: Array<string | null>, solutionString: string,
                       difficulty: DifficultyLevel, tags: Array<string>, hint: string | undefined, task?: string, optionsPerBlank?: Array<Array<string>|undefined>) {
        super({ _id, question, solution, solutionString, type: "FillBlanks", difficulty, tags, hint, task });
        
        // Safety check: ensure question is an array
        if (!Array.isArray(question)) {
            console.warn('FillBlanksExerciseQuestion: question is not an array, converting to array:', question);
            question = [question as any];
        }
        
        // Setze für jede Lücke (null) einen leeren String, für andere den Wert aus question
        this.currentSolution = question.map(q => {
            if (q === null || q === "null") {
                return "";
            }
            return q;
        }) as string[];
        
                 // Speichere die korrekten Lösungen separat für die Validierung
         this.correctSolutions = [];
        
                 this.optionsPerBlank = optionsPerBlank;
     }

     /**
      * Generiert die correctSolutions basierend auf der solution
      */
     private generateCorrectSolutions(): string[] {
         return this.question.map((q, index) => {
             if (q === null || q === "null") {
                 // Prüfe ob es sich um FillBlanks mit Options handelt
                 const hasOptions = this.optionsPerBlank && this.optionsPerBlank[index] && Array.isArray(this.optionsPerBlank[index]) && this.optionsPerBlank[index].length > 0;
                 
                 if (hasOptions) {
                     // Für FillBlanks mit Options: Verwende die solution direkt
                     if (this.solution && Array.isArray(this.solution) && this.solution.length > index) {
                         const solutionValue = this.solution[index];
                         // Behandle "null" String und null Wert gleich
                         if (solutionValue === null || solutionValue === "null") {
                             return "null";
                         }
                         return String(solutionValue || "");
                     }
                 } else {
                     // Für FillBlanks ohne Options: Extrahiere die korrekte Antwort aus der solution
                     if (this.solution && Array.isArray(this.solution) && this.solution.length > 0) {
                         // Verwende die erste Lösung aus dem solution-Array
                         const firstSolution = this.solution[0];
                         if (Array.isArray(firstSolution) && firstSolution.length > 0) {
                             const solutionSentence = firstSolution[0];
                             if (solutionSentence && typeof solutionSentence === 'string') {
                                 // Baue den kompletten Satz aus der solution
                                 const solutionWords = solutionSentence.split(' ');
                                 
                                 // Finde alle Lücken im question-Array
                                 const blankIndices = this.question.map((q, i) => (q === null || q === "null") ? i : -1).filter(i => i !== -1);
                                 
                                 // Finde die aktuelle Lücke
                                 const currentBlankIndex = blankIndices.indexOf(index);
                                 if (currentBlankIndex !== -1) {
                                     // Finde das entsprechende Wort in der Lösung
                                     let wordIndex = 0;
                                     for (let i = 0; i < this.question.length; i++) {
                                         if (i === index) {
                                             // Das ist die aktuelle Lücke
                                             if (wordIndex < solutionWords.length) {
                                                 const solutionWord = solutionWords[wordIndex];
                                                 const previousWord = this.question[i - 1];
                                                 if (previousWord && solutionWord.startsWith(previousWord)) {
                                                     const result = solutionWord.substring(previousWord.length);
                                                     return result;
                                                 } else {
                                                     // Fallback: Verwende das komplette Wort
                                                     return solutionWord;
                                                 }
                                             }
                                             break;
                                         }
                                         if (this.question[i] === null || this.question[i] === "null") {
                                             wordIndex++;
                                         } else {
                                             wordIndex++;
                                         }
                                     }
                                 }
                             }
                         }
                     }
                     
                     // Fallback: Verwende solutionString
                     if (this.solutionString && this.solutionString.trim()) {
                         // Finde alle Lücken im question-Array
                         const blankIndices = this.question.map((q, i) => (q === null || q === "null") ? i : -1).filter(i => i !== -1);
                         
                         // Finde die aktuelle Lücke
                         const currentBlankIndex = blankIndices.indexOf(index);
                         if (currentBlankIndex !== -1) {
                             // Finde das entsprechende Wort in der solutionString
                             const previousWord = this.question[index - 1];
                             if (previousWord) {
                                 const solutionWords = this.solutionString.split(' ');
                                 for (let i = 0; i < solutionWords.length; i++) {
                                     if (solutionWords[i].startsWith(previousWord)) {
                                         const result = solutionWords[i].substring(previousWord.length);
                                         return result;
                                     }
                                 }
                             }
                         }
                     }
                 }
                 
                 return "";
             }
             return q;
         }) as string[];
     }

    /**
     * Creates a new instance of this class from a json object.
     *
     * @param currentQuestion - The object to create a class instance from.
     */
    public static fromObject(currentQuestion: IFillBlanksExerciseQuestion & { options?: Array<string> | Array<Array<string>> }): FillBlanksExerciseQuestion {
        let optionsPerBlank = currentQuestion.optionsPerBlank;
        
        // Safety check: ensure question is an array
        if (!Array.isArray(currentQuestion.question)) {
            console.warn('FillBlanksExerciseQuestion.fromObject: question is not an array, converting to array:', currentQuestion.question);
            currentQuestion.question = [currentQuestion.question as any];
        }
        
        // Mapping: Wenn 'options' vorhanden sind, mappe sie auf die Lücken (null) im question-Array
        if (!optionsPerBlank && Array.isArray(currentQuestion.options) && Array.isArray(currentQuestion.question)) {
            optionsPerBlank = new Array(currentQuestion.question.length).fill(undefined);
            
                            // Prüfe ob options ein einfaches Array von Strings ist (wie ["der", "den", "dem"])
            const isSimpleStringArray = Array.isArray(currentQuestion.options) && 
                currentQuestion.options.length > 0 && 
                typeof currentQuestion.options[0] === 'string';
            
            if (isSimpleStringArray) {
                // Einfaches Array von Strings - alle Options gehören zur ersten Lücke
                const blankIndices = currentQuestion.question.map((q, index) => (q === null || q === "null") ? index : -1).filter(i => i !== -1);
                if (blankIndices.length > 0) {
                    // Alle Options auf die erste Lücke mappen
                    optionsPerBlank[blankIndices[0]] = currentQuestion.options as Array<string>;
                }
            } else {
                // Array von Arrays - verteile die Options
                let optionIdx = 0;
                for (let i = 0; i < currentQuestion.question.length; i++) {
                    if ((currentQuestion.question[i] === null || currentQuestion.question[i] === "null") && optionIdx < currentQuestion.options.length) {
                        const opts = currentQuestion.options[optionIdx];
                        optionsPerBlank[i] = Array.isArray(opts) ? opts : [opts];
                        optionIdx++;
                    }
                }
            }
        } else if (!optionsPerBlank) {
            // Fallback: Wenn keine optionsPerBlank vorhanden sind, aber es Lücken gibt,
            // erstelle leere Arrays für die Lücken (damit sie als "ohne Options" erkannt werden)
            if (Array.isArray(currentQuestion.question) && currentQuestion.question.some(q => q === null || q === "null")) {
                optionsPerBlank = new Array(currentQuestion.question.length).fill(undefined);
            } else {
                optionsPerBlank = undefined;
            }
        }
        
        const instance = new FillBlanksExerciseQuestion(
            currentQuestion._id,
            currentQuestion.question,
            currentQuestion.solution,
            currentQuestion.solutionString,
            currentQuestion.difficulty,
            currentQuestion.tags,
            currentQuestion.hint,
            currentQuestion.task,
            optionsPerBlank
        );
        
                 // Wenn correctSolutions in den Daten vorhanden sind, überschreibe die automatisch generierten
         if ((currentQuestion as any).correctSolutions && Array.isArray((currentQuestion as any).correctSolutions)) {
             instance.correctSolutions = (currentQuestion as any).correctSolutions;
         } else {
             // Generiere correctSolutions basierend auf der solution
             instance.correctSolutions = instance.generateCorrectSolutions();
         }
        
        return instance;
    }

    /**
     * Fills the blank or current word at the given index by replacing it with the new word (or null).
     *
     * @param index        - The index where the blank should be filled.
     * @param wordToFillIn - The word or not word to fill in.
     */
    public fillWordAtIndex(index: number, wordToFillIn: string | null): boolean {
        if (!this.currentSolution || index >= this.currentSolution.length) {
            return false;
        }
        if(wordToFillIn == "" || wordToFillIn == null)
        {
            wordToFillIn = "";
        }
        this.currentSolution[index] = wordToFillIn ?? "";
        //console.log("wordtofillin: " + wordToFillIn + " part: " + this.currentSolution[index]);
        return true;
    }

    /**
     * Returns if the word at the given index should be left blank.
     *
     * @param index - The index within the words array.
     */
    public isBlankAtIndex(index: number): boolean {
        const value = this.question[index];
        return value === null || value === "null";
    }

    /**
     * Returns if the word at the given index is not blank.
     *
     * @param index - The index within the words array.
     */
    public isWordAtIndex(index: number): boolean {
        const value = this.question[index];
        return typeof value === "string" && value !== "null";
    }

    /**
     * Checks if there are still blank parts left. (Nullish -> null | undefined | "" | -1 | etc)
     */
    public isCompletelyFilled(): boolean {
        return !!this.currentSolution?.every(val => !!val);
    }

    /**
     * Returns the indexes of the false parts.
     */
    public getFalseParts(): Array<number> {
        const falseParts: Array<number> = [];
        if (!this.currentSolution) {
            return [];
        }

        // Prüfe ob es sich um FillBlanks mit Options handelt
        // Ein FillBlanks hat Options, wenn mindestens eine Lücke Options hat
        const hasOptions = this.optionsPerBlank && this.optionsPerBlank.some(options => 
            options && Array.isArray(options) && options.length > 0
        );

        if (hasOptions) {
            // Für FillBlanks mit Options: Vergleiche alle Elemente
            if (!this.correctSolutions) {
                return [];
            }
            
            for (let i = 0; i < this.currentSolution.length; i++) {
                const userAnswer = String(this.currentSolution[i] || '').trim();
                const correctAnswer = String(this.correctSolutions[i] || '').trim();
                
                // Vergleiche case-insensitive und ignoriere Leerzeichen
                if (userAnswer.toLowerCase() !== correctAnswer.toLowerCase()) {
                    if (correctAnswer === "" && userAnswer !== "") {
                        // Lösung ist leer, aber User hat etwas eingegeben
                        // Das ist falsch - wenn die Lösung leer ist, sollte auch die User-Antwort leer sein
                        falseParts.push(i);
                    } else {
                        falseParts.push(i);
                    }
                }
            }
            return falseParts;
        } else {
            // Für FillBlanks ohne Options: Prüfe ob der komplette Satz korrekt ist
            if (this.solution && Array.isArray(this.solution) && this.solution.length > 0) {
                const userSentence = this.currentSolution.join(' ').trim();
                let isCorrect = false;
                
                // Prüfe gegen alle möglichen Lösungen
                for (const solutionArray of this.solution) {
                    if (Array.isArray(solutionArray) && solutionArray.length > 0) {
                        const correctSentence = solutionArray[0].trim();
                        if (userSentence.toLowerCase() === correctSentence.toLowerCase()) {
                            isCorrect = true;
                            break;
                        }
                    }
                }
                
                // Wenn der Satz nicht korrekt ist, markiere alle Lücken als falsch
                if (!isCorrect) {
                    for (let i = 0; i < this.question.length; i++) {
                        if (this.question[i] === null || this.question[i] === "null") {
                            falseParts.push(i);
                        }
                    }
                }
                
                return falseParts;
            }

            // Fallback: Vergleiche einzelne Lücken
            if (!this.correctSolutions) {
                return [];
            }
            
            for (let i = 0; i < this.currentSolution.length; i++) {
                if (this.question[i] === null || this.question[i] === "null") { // Nur Lücken vergleichen!
                    const userAnswer = String(this.currentSolution[i] || '').trim();
                    const correctAnswer = String(this.correctSolutions[i] || '').trim();
                    
                    // Vergleiche case-insensitive und ignoriere Leerzeichen
                    if (userAnswer.toLowerCase() !== correctAnswer.toLowerCase()) {
                        if (correctAnswer === "" && userAnswer !== "") {
                            // Lösung ist leer, aber User hat etwas eingegeben
                            // Das ist falsch - wenn die Lösung leer ist, sollte auch die User-Antwort leer sein
                            falseParts.push(i);
                        } else {
                            falseParts.push(i);
                        }
                    }
                }
            }
            return falseParts;
        }
    }

         /**
      * Override isSolutionCorrect to use correctSolutions instead of solution
      */
     public isSolutionCorrect(): boolean {
         if (!this.currentSolution) {
             return false;
         }

         // Prüfe ob es sich um FillBlanks mit Options handelt
         const hasOptions = this.optionsPerBlank && this.optionsPerBlank.some(options => 
             options && Array.isArray(options) && options.length > 0
         );

         if (hasOptions) {
             // Für FillBlanks mit Options: Vergleiche alle Elemente
             if (!this.correctSolutions) {
                 return false;
             }
             
             for (let i = 0; i < this.currentSolution.length; i++) {
                 const userAnswer = (this.currentSolution[i] || "").trim();
                 const correctAnswer = (this.correctSolutions[i] || "").trim();
                 
                 // Vergleiche case-insensitive und ignoriere Leerzeichen
                 if (userAnswer.toLowerCase() !== correctAnswer.toLowerCase()) {
                     return false;
                 }
             }
             return true;
         } else {
             // Für FillBlanks ohne Options: Vergleiche nur die Lücken (null im question-Array) mit den correctSolutions
             for (let i = 0; i < this.question.length; i++) {
                 if (this.question[i] === null || this.question[i] === "null") { // Nur Lücken vergleichen!
                     const userAnswer = (this.currentSolution[i] || "").trim();
                     
                     // Prüfe ob es mehrere mögliche Lösungen gibt
                     if (this.solution && Array.isArray(this.solution) && this.solution.length > 1) {
                         // Es gibt mehrere Lösungen - prüfe gegen alle
                         let isCorrect = false;
                         
                         for (const solutionArray of this.solution) {
                             if (Array.isArray(solutionArray) && solutionArray.length > 0) {
                                 const solutionSentence = solutionArray[0];
                                 if (solutionSentence && typeof solutionSentence === 'string') {
                                     const solutionWords = solutionSentence.split(' ');
                                     
                                     // Finde die Position der aktuellen Lücke
                                     const blankIndices = this.question.map((q, idx) => (q === null || q === "null") ? idx : -1).filter(idx => idx !== -1);
                                     const currentBlankIndex = blankIndices.indexOf(i);
                                     
                                     if (currentBlankIndex !== -1) {
                                         // Finde das entsprechende Wort in der Lösung
                                         let wordIndex = 0;
                                         for (let j = 0; j < this.question.length; j++) {
                                             if (j === i) {
                                                 // Das ist die aktuelle Lücke
                                                 if (wordIndex < solutionWords.length) {
                                                     const solutionWord = solutionWords[wordIndex];
                                                     const previousWord = this.question[j - 1];
                                                     
                                                     let expectedAnswer: string;
                                                     if (previousWord && solutionWord.startsWith(previousWord)) {
                                                         expectedAnswer = solutionWord.substring(previousWord.length);
                                                     } else {
                                                         expectedAnswer = solutionWord;
                                                     }
                                                     
                                                     if (userAnswer.toLowerCase() === expectedAnswer.toLowerCase()) {
                                                         isCorrect = true;
                                                         break;
                                                     }
                                                 }
                                                 break;
                                             }
                                             if (this.question[j] === null || this.question[j] === "null") {
                                                 wordIndex++;
                                             } else {
                                                 wordIndex++;
                                             }
                                         }
                                     }
                                 }
                             }
                             if (isCorrect) break;
                         }
                         
                         if (!isCorrect) {
                             return false;
                         }
                     } else {
                         // Standard-Vergleich mit correctSolutions
                         const correctAnswer = (this.correctSolutions[i] || "").trim();
                         
                         // Spezielle Behandlung für "null" String vs null Wert
                         if (userAnswer.toLowerCase() === "null" && correctAnswer.toLowerCase() === "null") {
                             continue; // Beide sind "null", also korrekt
                         }
                         
                         // Vergleiche case-insensitive und ignoriere Leerzeichen
                         if (userAnswer.toLowerCase() !== correctAnswer.toLowerCase()) {
                             return false;
                         }
                     }
                 }
             }
             return true;
         }
     }
}

export interface ICorrectionExerciseQuestion extends IExerciseQuestion<Array<string>, string> {
    /** Optional: Array von Auswahlmöglichkeiten (Dropdown), oder undefined für normale Eingabe. */
    options?: Array<string> | Array<Array<string>>;
}

/**
 * Class to be used for exercises where an input has to be checked for correctness.
 */
export class CorrectionExerciseQuestion extends ExerciseQuestion<Array<string>, string>
            implements ICorrectionExerciseQuestion {
    public constructor(_id: string | undefined, question: Array<string>, solution: string, solutionString: string,
                       difficulty: DifficultyLevel, tags: Array<string>, hint: string | undefined, task?: string, options?: Array<string> | Array<Array<string>>) {
        super({ _id, question, solution, solutionString, type: "Correction", difficulty, tags, hint, task });
        this.currentSolution = "";
        this.options = options;
    }

    /**
     * Creates a new instance of this class from a json object.
     *
     * @param currentQuestion - The object to create a class instance from.
     */
    public static fromObject(currentQuestion: ICorrectionExerciseQuestion): CorrectionExerciseQuestion {
        return new CorrectionExerciseQuestion(currentQuestion._id, currentQuestion.question, currentQuestion.solution,
            currentQuestion.solutionString, currentQuestion.difficulty, currentQuestion.tags, currentQuestion.hint, currentQuestion.task, currentQuestion.options);
    }

    /**
     * Checks if the given answer is equals the solution.
     */
    public isSolutionCorrect(): boolean {
        this.userAnswer = this.currentSolution;
        if (!this.currentSolution) {
            return false;
        }
        // Direkter Vergleich der Eingabe mit der Lösung
        return String(this.currentSolution).trim() === String(this.solution).trim();
    }
}


export function exerciseQuestionFromJson(question: IExerciseQuestionType): ExerciseQuestionType {
    const isCorrection = (q: IExerciseQuestion<any, any>): q is ICorrectionExerciseQuestion => q.type === "Correction";
    const isOrder = (q: IExerciseQuestion<any, any>): q is IOrderExerciseQuestion => q.type === "Order";
    const isMultipleChoice = (q: IExerciseQuestion<any, any>): q is IMultipleChoiceExerciseQuestion => q.type === "MultipleChoice";
    const isQuiz = (q: IExerciseQuestion<any, any>): q is IQuizExerciseQuestion => q.type === "Quiz";
    // const isFillBlanks = (q: IExerciseQuestion<any, any>): q is IFillBlanksExerciseQuestion => q.type === "FillBlanks";

    if (isCorrection(question)) {
        return CorrectionExerciseQuestion.fromObject(question);
    }
    if (isOrder(question)) {
        return OrderExerciseQuestion.fromObject(question);
    }
    if (isMultipleChoice(question)) {
        return MultipleChoiceExerciseQuestion.fromObject(question);
    }
    if (isQuiz(question)) {
        return QuizExerciseQuestion.fromObject(question);
    }
    return FillBlanksExerciseQuestion.fromObject(question);
}
